import * as React from "react";
import { observer } from "mobx-react";
import { withStores } from "stores";
import { RouteStore } from "bernie-core";
import { Viewport, ViewSmall, ViewMedium } from "@shared-ui/viewport-context";

import {
  UitkLayoutGrid,
  UitkLayoutConditionalGridSpan,
  UitkLayoutConditionalGridColumnRow,
} from "uitk-react-layout-grid";
import { UitkSpacing } from "uitk-react-spacing";

import { SubmitRow } from "components/shared/StorefrontWizard/SubmitRow/SubmitRow";
import { LobErrorSummary } from "components/shared/LobErrorSummary/LobErrorSummary";
import { Dates } from "components/shared/Dates/Dates";
import { TimeDropdown } from "components/shared/TimeDropdown/TimeDropdown";
import { Travelers } from "components/shared/Travelers/Travelers";
import { TravelersTriggerTypes } from "components/shared/TravelersField/typings";
import { CarWizardState } from "stores/wizard/state/car/CarWizardState";
import { GlobalConfig } from "stores/wizard/config";
import { GtConfig } from "stores/wizard/config/staticConfigs/car/typings";

import { locKeys } from "../l10n";
import { GTHiddenSearchData } from "./GTHiddenSearchData";
import { BookRoundtrip } from "./BookRoundtrip";
import { GroundTransportLocation } from "./Location/GroundTransportLocation";
import { BookRoundtripToggle } from "./BookRoundtripToggle";

export interface GroundTransportationPWAViewProps {
  carWizardState: CarWizardState;
  toggleBookRoundtrip: () => void;
  setInputsRefs?: (ref: React.RefObject<HTMLInputElement>) => void;
  getLocError: (isLink: boolean) => string | undefined;
  gtConfig: GtConfig;
  routeStore: RouteStore;
  globalConfig?: GlobalConfig;
  showGTOnly: boolean;
}

export const GroundTransportPWAView = withStores("routeStore")(
  observer((props: GroundTransportationPWAViewProps) => {
    const {
      carWizardState,
      carWizardState: {
        pickupTime,
        isGroundTransportFormValid,
        returnTime,
        roundTrip,
        groundTransportationLocationState,
        updateInvalidFormsState,
      },
      setInputsRefs,
      toggleBookRoundtrip,
      routeStore,
      showGTOnly,
    } = props;
    const { gtArrivalTime, gtDepartureTime, gtArrivalDate, gtDepartureDate } = locKeys;
    const { isOriginAirport, updateFieldsFromQueryParams, airportCode, hotelId } = groundTransportationLocationState;
    const format12hrConfig = carWizardState.config.timeFormats.timeFormat12Hour;

    const mainGridLayout: UitkLayoutConditionalGridColumnRow = { small: 1, medium: 12 };

    const errorSummaryGridColumns: UitkLayoutConditionalGridSpan = { small: 2, medium: 12 };
    const travelersColSpan: UitkLayoutConditionalGridSpan = { small: 1, large: 12 };
    const locationColSpan: UitkLayoutConditionalGridSpan = { small: 1, medium: 12, large: 6 };
    const datesColSpan: UitkLayoutConditionalGridSpan = { small: 1, medium: 6, large: 3 };
    const timeDropdownColSpan: UitkLayoutConditionalGridSpan = { small: 1, medium: 6, large: 3 };

    const roundTripDatesColSpan: UitkLayoutConditionalGridSpan = { small: 1, medium: 6, large: 3 };
    const roundTripTimeColSpan: UitkLayoutConditionalGridSpan = { small: 1, medium: 6, large: 3 };

    if (isGroundTransportFormValid) {
      updateInvalidFormsState();
    }
    updateFieldsFromQueryParams(routeStore);
    const dateLabel = isOriginAirport ? gtArrivalDate : gtDepartureDate;
    const timeLabel = isOriginAirport ? gtArrivalTime : gtDepartureTime;
    carWizardState.config.gtConfig.date.start.labelToken = dateLabel;

    return (
      <>
        <UitkSpacing margin={{ blockstart: "one" }}>
          <UitkLayoutGrid columns={mainGridLayout} space="three">
            <>
              {!isGroundTransportFormValid && (
                <LobErrorSummary
                  locHeadingToken={props.getLocError(false)!}
                  locHeadingArgs={carWizardState.moreThanOneError() && [carWizardState.numberOfErrors]}
                  locLinkToken={props.getLocError(true)}
                  linkClickFocusId="id-roundtrip-error"
                  inputErrorRef={carWizardState.errorInputRef}
                  colSpan={errorSummaryGridColumns}
                  setInputsRefs={setInputsRefs}
                />
              )}

              <Viewport>
                <ViewSmall>
                  <Travelers
                    lobState={carWizardState}
                    colSpan={travelersColSpan}
                    config={props.gtConfig.travelers}
                    triggerType={TravelersTriggerTypes.LINK_BUTTON}
                  />
                </ViewSmall>
                {showGTOnly ? (
                  <ViewMedium>
                    <Travelers
                      lobState={carWizardState}
                      colSpan={travelersColSpan}
                      config={props.gtConfig.travelers}
                      triggerType={TravelersTriggerTypes.LINK_BUTTON}
                    />
                  </ViewMedium>
                ) : (
                  <ViewMedium />
                )}
              </Viewport>

              <GroundTransportLocation
                groundTransportationLocationState={groundTransportationLocationState}
                colSpan={locationColSpan}
                setInputsRefs={setInputsRefs}
                lobState={carWizardState}
              />
              <Dates lobState={carWizardState} singleDate colSpan={datesColSpan} setInputsRefs={setInputsRefs} />
              <TimeDropdown
                labelKey={timeLabel}
                timeValues={carWizardState.config.gtConfig.timeValues}
                onChange={carWizardState.setPickupTime}
                value={pickupTime}
                invalidKey={carWizardState.timeStartInvalidKey}
                colSpan={timeDropdownColSpan}
                use12hr={format12hrConfig}
                showTimeIcon
              />
              {isOriginAirport && (
                <BookRoundtripToggle toggleBookRoundtrip={toggleBookRoundtrip} roundTripChecked={roundTrip} />
              )}
              {isOriginAirport && roundTrip && (
                <BookRoundtrip
                  carWizardState={carWizardState}
                  datesColSpan={roundTripDatesColSpan}
                  timeColSpan={roundTripTimeColSpan}
                  setInputsRefs={setInputsRefs}
                />
              )}
            </>
          </UitkLayoutGrid>
        </UitkSpacing>
        <SubmitRow lobState={props.carWizardState} globalConfig={props.globalConfig} />
        <GTHiddenSearchData
          pickUpTime={pickupTime}
          dropOffTime={roundTrip ? returnTime : ""}
          direction={isOriginAirport}
          airportCode={airportCode}
          hotelId={hotelId}
        />
      </>
    );
  })
);
